import { api } from "@breakout/backend/convex/_generated/api"
import type { Maybe } from "@breakout/shared/types/market"
import { convexQuery } from "@convex-dev/react-query"
import { useQuery } from "@tanstack/react-query"
import type { FunctionReturnType } from "convex/server"
import type { Address } from "viem"

interface BuyQuoteArgs {
	marketAddress: Address
	bucket: number
	usdAmount: number
}

export type BuyQuoteResult = FunctionReturnType<typeof api.indexer.public.quoteBuy>

/**
 * Get buy quote using indexed market data (reactive via Convex).
 *
 * Benefits over RPC:
 * - No polling needed: auto-updates when market state changes
 * - No RPC cost: uses indexed data
 * - Faster response: cached at edge
 *
 * Trade-off: ~3s latency vs RPC. Shows "estimated" cost.
 * For actual trade execution, SDK's prepareBuy() uses fresh RPC data.
 */
export function useBuyQuote(args: Maybe<BuyQuoteArgs>) {
	const enabled = !!args && args.usdAmount > 0

	return useQuery({
		...convexQuery(api.indexer.public.quoteBuy, {
			marketAddress: args?.marketAddress ?? "",
			bucket: args?.bucket ?? 0,
			usdAmount: args?.usdAmount ?? 0,
		}),
		enabled,
		// No staleTime/refetchInterval needed - Convex is reactive
	})
}
